/** Screen scaffolding shared by every screen: the title block, the five states (docs/52 §5) and the "i" sheet. */
import { Banner, Button, EmptyState, Icon, InfoButton, Sheet, Skeleton } from '@ff/app-ui';
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ApiError } from '../lib/api.ts';
import { useOnline, type QueryState } from '../lib/data.ts';
import { ago } from '../lib/format.ts';
import { back as goBack } from '../lib/router.ts';
import { useStore } from '../lib/store.ts';
import { tz } from '../lib/ui.ts';
import { S } from '@ff/app-ui/copy';

/** "Updated 2 minutes ago", ticking once a minute. */
function useNow(ms = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function Screen(p: {
  title: string;
  /** Hide the h1 visually (Home shows a greeting instead) but keep it for screen readers. */
  titleHidden?: boolean;
  back?: string;
  lead?: ComponentChildren;
  query?: Pick<QueryState<unknown>, 'refreshing' | 'updatedAt' | 'refetch' | 'loading'> | null;
  actions?: ComponentChildren;
  children: ComponentChildren;
  wide?: boolean;
}): JSX.Element {
  const h1 = useRef<HTMLHeadingElement>(null);
  const now = useNow();
  const zone = useStore(tz);
  useEffect(() => {
    document.title = `${p.title} · ${S.app.name}`;
    // Focus the title after navigation so screen readers announce the new screen (not on first load).
    if (document.body.dataset.navigated) h1.current?.focus({ preventScroll: true });
    document.body.dataset.navigated = '1';
  }, [p.title]);
  const q = p.query;
  return (
    <div class="screen" data-wide={p.wide ? '' : undefined}>
      {p.back && (
        <a class="back-link" href={p.back} onClick={(e) => (e.preventDefault(), goBack(p.back!))}>
          <Icon name="chevron-left" />
          <span>{S.app.back}</span>
        </a>
      )}
      <header class="screen-head">
        <div class="screen-head-row">
          <h1 ref={h1} tabIndex={-1} class={p.titleHidden ? 'sr-only' : undefined}>
            {p.title}
          </h1>
          {p.actions && <div class="screen-actions">{p.actions}</div>}
        </div>
        {q && (q.refreshing || q.updatedAt) && !q.loading ? (
          <p class="screen-stamp" aria-live="polite">
            {q.refreshing ? (
              <span>{S.app.updating}</span>
            ) : (
              <>
                <span>{S.app.updated(ago(new Date(q.updatedAt!).toISOString(), zone, now))}</span>
                <button type="button" class="stamp-refresh" onClick={() => void q.refetch()}>
                  <Icon name="refresh" size={18} />
                  <span>{S.app.refresh}</span>
                </button>
              </>
            )}
          </p>
        ) : null}
        {p.lead && <p class="screen-lead">{p.lead}</p>}
      </header>
      {p.children}
    </div>
  );
}

/**
 * Renders the right state for a read: skeleton on a true first load (then a plain line after a second), the
 * saved copy with an error note when a refresh fails, or a full error with Try again when there's nothing saved.
 */
export function Loaded<T>(p: { q: QueryState<T>; skeleton?: 'rows' | 'cards' | 'detail'; children: (data: T) => ComponentChildren }): JSX.Element {
  const onlineNow = useOnline();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!p.q.loading) return;
    const t = setTimeout(() => setSlow(true), 1000);
    return () => clearTimeout(t);
  }, [p.q.loading]);
  if (p.q.data !== undefined)
    return (
      <>
        {p.q.error && !p.q.refreshing && <RefreshError error={p.q.error} onRetry={p.q.refetch} />}
        {p.children(p.q.data)}
      </>
    );
  if (p.q.error) return <LoadError error={p.q.error} onRetry={p.q.refetch} />;
  if (slow) return <p class="still-loading" role="status">{onlineNow ? S.app.stillLoading : S.app.offlineEmpty}</p>;
  return <SkeletonBlock kind={p.skeleton ?? 'rows'} />;
}

export function SkeletonBlock({ kind }: { kind: 'rows' | 'cards' | 'detail' }): JSX.Element {
  return (
    <div class="stack" aria-busy="true" aria-label={S.app.loading}>
      {kind === 'cards' && [0, 1, 2].map((i) => <Skeleton key={i} kind="card" />)}
      {kind === 'rows' && (
        <div class="list">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} kind="row" />
          ))}
        </div>
      )}
      {kind === 'detail' && (
        <>
          <Skeleton kind="line" width="60%" />
          <Skeleton kind="line" width="40%" />
          <Skeleton kind="card" />
        </>
      )}
    </div>
  );
}

function RefreshError({ error, onRetry }: { error: ApiError; onRetry: () => Promise<void> }) {
  if (error.offline) return null; // the offline bar already says it
  return (
    <div class="refresh-error">
      <Banner tone="warn" title={error.message} action={<Button size="sm" onClick={() => void onRetry()} icon="refresh">{S.app.tryAgain}</Button>} />
    </div>
  );
}

export function LoadError({ error, onRetry }: { error: ApiError; onRetry?: () => Promise<void> }): JSX.Element {
  if (error.code === 'not_found')
    return <EmptyState title={S.app.notFoundTitle} body={S.app.notFoundBody} action={<Button href="/" variant="secondary">{S.app.goHome}</Button>} />;
  if (error.code === 'forbidden') return <EmptyState title={error.message} />;
  return (
    <EmptyState
      title={error.offline ? S.app.offline : S.app.errorTitle}
      body={error.offline ? S.app.offlineEmpty : `${error.message} ${S.app.errorBody}`}
      action={onRetry ? <Button variant="secondary" icon="refresh" onClick={() => void onRetry()}>{S.app.tryAgain}</Button> : null}
    />
  );
}

/** The "i" beside every number (docs/52 D3): what it is, where it's from, what it isn't. */
export function Info(p: { title: string | undefined; lines: string[] }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <InfoButton label={S.app.info(p.title ?? "")} onClick={() => setOpen(true)} />
      <Sheet open={open} onClose={() => setOpen(false)} title={p.title ?? ""} closeLabel={S.app.close}>
        <div class="stack" data-gap="m">
          {p.lines.map((l, i) => (
            <p key={i}>{l}</p>
          ))}
        </div>
      </Sheet>
    </>
  );
}

export function Section(p: { title: ComponentChildren; id?: string; aside?: ComponentChildren; children: ComponentChildren; info?: JSX.Element | null }): JSX.Element {
  const id = p.id ?? `sec-${String(p.title).toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`;
  return (
    <section class="section" aria-labelledby={id}>
      <div class="list-head">
        <h2 id={id}>{p.title}</h2>
        <div class="cluster">
          {p.aside}
          {p.info}
        </div>
      </div>
      {p.children}
    </section>
  );
}

/** The one primary action per tab, in the thumb zone above the tab bar (docs/52 D1). */
export function PrimaryAction(p: { href?: string; onClick?: () => void; icon?: string; children: ComponentChildren }): JSX.Element {
  return (
    <div class="primary-action">
      <Button variant="primary" size="lg" href={p.href} onClick={p.onClick} icon={p.icon ?? 'plus'}>
        {p.children}
      </Button>
    </div>
  );
}

export function Stars({ rating, label }: { rating: number; label: string }): JSX.Element {
  return (
    <span class="stars" role="img" aria-label={label}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Icon key={i} name="star" size={18} filled={i <= rating} class={i <= rating ? 'star-on' : 'star-off'} />
      ))}
    </span>
  );
}
